import React, { useEffect } from 'react';

/**
 * Hook personalizado para aplicar estilos de scroll a elementos
 * @param {string} scrollType - Tipo de scroll: 'custom', 'thin', 'horizontal'
 * @param {object} ref - Ref del elemento DOM
 */
export const useScrollStyles = (scrollType = 'custom', ref = null) => {
  useEffect(() => {
    if (!ref?.current) return;

    const element = ref.current;
    const scrollClasses = {
      custom: 'custom-scrollbar',
      thin: 'thin-scrollbar', 
      horizontal: 'horizontal-scroll'
    };

    const className = scrollClasses[scrollType] || scrollClasses.custom;
    
    // Agregar clase de scroll
    element.classList.add(className);

    // Cleanup
    return () => {
      element.classList.remove(className);
    };
  }, [scrollType, ref]);
};

/**
 * Función helper para aplicar clases de scroll directamente a elementos
 * @param {string} scrollType - Tipo de scroll
 * @returns {string} - Nombre de clase CSS
 */
export const getScrollClass = (scrollType = 'custom') => {
  const scrollClasses = {
    custom: 'custom-scrollbar',
    thin: 'thin-scrollbar',
    horizontal: 'horizontal-scroll'
  };
  
  return scrollClasses[scrollType] || scrollClasses.custom;
};

/**
 * Hook para detectar si el scroll es necesario
 * @param {object} ref - Ref del elemento DOM
 * @param {string} direction - 'vertical' o 'horizontal'
 * @returns {boolean} - Si el scroll es necesario
 */
export const useScrollDetection = (ref, direction = 'vertical') => {
  const [isScrollable, setIsScrollable] = React.useState(false);

  useEffect(() => {
    if (!ref?.current) return;

    const element = ref.current;
    
    const checkScroll = () => {
      if (direction === 'vertical') {
        setIsScrollable(element.scrollHeight > element.clientHeight);
      } else {
        setIsScrollable(element.scrollWidth > element.clientWidth);
      }
    };

    checkScroll();

    // Observer para cambios en el contenido
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [ref, direction]);

  return isScrollable;
};

/**
 * Detecta en vivo si a un contenedor con scroll le queda contenido oculto
 * arriba/abajo — a diferencia de `useScrollDetection` (que solo chequea SI es
 * scrolleable, una vez), esto se actualiza con cada scroll para poder mostrar
 * un indicador visual (sombra/gradiente) de "hay más contenido".
 *
 * Por qué existe: en macOS, sobre todo en Safari, el sistema operativo oculta
 * la barra de scroll nativa hasta que el usuario interactúa (scrollbar
 * "overlay"), y esa preferencia NO se puede forzar por CSS — no es un bug de
 * esta app, es una restricción de la plataforma (Ajustes del Sistema >
 * General > "Mostrar barras de desplazamiento"). El scroll en sí funciona
 * igual (trackpad/mouse), pero sin la barra visible el usuario no tiene forma
 * de saber que hay más contenido debajo. La sombra que da este hook es
 * independiente del navegador/SO — no depende de la barra nativa en absoluto.
 *
 * @param {object} scrollRef - Ref del elemento con overflow:auto/scroll
 * @param {object} [contentRef] - Ref de un wrapper alrededor del CONTENIDO
 *   (no del contenedor con scroll). El contenedor con scroll normalmente
 *   ocupa todo el viewport y su propio tamaño casi nunca cambia, así que un
 *   ResizeObserver sobre él NO detecta cuando el contenido de adentro
 *   crece/achica (ej: datos que llegan async, o cambia la ruta). Si no se
 *   pasa, se observa `scrollRef` igual (mejor que nada, pero puede no
 *   reaccionar a cambios de contenido — pasar `contentRef` si el consumidor
 *   ya tiene ese wrapper disponible).
 * @param {number} threshold - Margen en px para considerar "llegó al borde" (evita parpadeo por redondeo de subpíxel)
 * @returns {{ hasMoreAbove: boolean, hasMoreBelow: boolean }}
 */
export const useScrollEdgeShadow = (scrollRef, contentRef, threshold = 4) => {
  const [hasMoreAbove, setHasMoreAbove] = React.useState(false);
  const [hasMoreBelow, setHasMoreBelow] = React.useState(false);

  useEffect(() => {
    const scrollEl = scrollRef?.current;
    if (!scrollEl) return;

    const update = () => {
      setHasMoreAbove(scrollEl.scrollTop > threshold);
      setHasMoreBelow(scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight > threshold);
    };

    update();
    scrollEl.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(contentRef?.current || scrollEl);

    return () => {
      scrollEl.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      resizeObserver.disconnect();
    };
  }, [scrollRef, contentRef, threshold]);

  return { hasMoreAbove, hasMoreBelow };
};

export default useScrollStyles;